import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/layout/Header';
import ErrorBanner from '../../components/ui/ErrorBanner';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import apiClient from '../../api/apiClient';
import { st, colors } from './shared/meetingStyles';
import CheckInSection from './sections/CheckInSection';
import GoalsSection from './sections/GoalsSection';
import ScorecardSection from './sections/ScorecardSection';
import HeadlinesSection from './sections/HeadlinesSection';
import TodosSection from './sections/TodosSection';
import IssuesSection from './sections/IssuesSection';
import WrapUpSection from './sections/WrapUpSection';
import LastMeetingSummarySection from './sections/LastMeetingSummarySection';
import ParticipantsPanel from './sections/ParticipantsPanel';
import TranscriptSection from './sections/TranscriptSection';
import {
  CompassIcon, ClipboardIcon, SmileIcon, TargetIcon, BarChartIcon, MegaphoneIcon,
  CheckSquareIcon, BulbIcon, FlagIcon, UsersIcon, PlusIcon, PlayIcon, FileTextIcon,
} from './shared/Icons';

const heroGhostBtn = {
  padding: '0.45rem 0.85rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)',
  borderRadius: '0.6rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', color: '#e5e7eb',
  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
};

const TABS = [
  { key: 'summary', label: 'Summary', Icon: ClipboardIcon },
  { key: 'checkin', label: 'Check-in', Icon: SmileIcon },
  { key: 'transcript', label: 'Transcript', Icon: FileTextIcon },
  { key: 'goals', label: 'Goals', Icon: TargetIcon },
  { key: 'metrics', label: 'Metrics', Icon: BarChartIcon },
  { key: 'headlines', label: 'Shout outs', Icon: MegaphoneIcon },
  { key: 'todos', label: 'To-dos', Icon: CheckSquareIcon },
  { key: 'issues', label: 'Issues', Icon: BulbIcon },
  { key: 'wrapup', label: 'Wrap-up', Icon: FlagIcon },
];

function NewMeetingForm({ onCreate, onCancel }) {
  const [name, setName] = useState('');
  const [cadence, setCadence] = useState('weekly');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onCreate({ name: name.trim(), cadence });
      onCancel();
    } catch {} finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} style={{ ...st.card, display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Meeting name" style={{ ...st.input, flex: 1 }} autoFocus />
      <select value={cadence} onChange={e => setCadence(e.target.value)} style={{ ...st.input, width: '130px' }}>
        <option value="weekly">Weekly</option>
        <option value="biweekly">Biweekly</option>
        <option value="monthly">Monthly</option>
      </select>
      <button type="submit" disabled={saving} style={st.primaryBtn}>{saving ? '...' : 'Create'}</button>
      <button type="button" onClick={onCancel} style={st.secondaryBtn}>Cancel</button>
    </form>
  );
}

export default function MeetingsPage() {
  const [meetings, setMeetings] = useState([]);
  const [meetingId, setMeetingId] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('summary');
  const [starting, setStarting] = useState(false);
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);
  const [showNewMeeting, setShowNewMeeting] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);

  const meeting = meetings.find(m => m.id === meetingId) || null;

  const loadMeetings = useCallback(async (selectId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/meetings');
      const list = res.data.meetings || [];
      setMeetings(list);
      const chosen = selectId && list.some(m => m.id === selectId) ? selectId : list[0]?.id || null;
      setMeetingId(chosen);
      if (chosen) {
        const sessionRes = await apiClient.get(`/meetings/${chosen}/sessions/active`);
        setSession(sessionRes.data.session);
      } else {
        setSession(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMeetings(); }, [loadMeetings]);

  const switchMeeting = async (id) => {
    setMeetingId(id);
    setTab('summary');
    setShowParticipants(false);
    try {
      const sessionRes = await apiClient.get(`/meetings/${id}/sessions/active`);
      setSession(sessionRes.data.session);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const createMeeting = async (data) => {
    const res = await apiClient.post('/meetings', data);
    await loadMeetings(res.data.id);
  };

  const startMeeting = async () => {
    setStarting(true);
    try {
      const res = await apiClient.post(`/meetings/${meeting.id}/sessions`);
      setSession(res.data);
      setTab('checkin');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setStarting(false);
    }
  };

  const onMeetingEnded = () => {
    setSession(null);
    setSummaryRefreshKey(k => k + 1);
    setTab('summary');
  };

  if (loading) return (<div><Header title="L10 Meeting" /><div style={st.page}><LoadingSpinner /></div></div>);

  return (
    <div>
      <Header title="L10 Meeting" />
      <div style={st.page}>
        {error && <ErrorBanner message={error} />}

        <div style={{
          borderRadius: '1rem', padding: '1.4rem 1.5rem',
          background: `linear-gradient(135deg, #0d1f0d, #16321a 60%, #1d3d20)`,
          boxShadow: '0 4px 20px rgba(13,31,13,0.25)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '0.75rem', flexShrink: 0,
              background: 'rgba(201,168,75,0.15)', border: '1px solid rgba(201,168,75,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem',
            }}><CompassIcon size={22} style={{ color: '#c9a84b' }} /></div>
            <div>
              <select value={meetingId || ''} onChange={e => switchMeeting(Number(e.target.value))} style={{
                background: 'transparent', border: 'none', color: '#fff', fontWeight: '800', fontSize: '1.05rem',
                outline: 'none', cursor: 'pointer', padding: 0, appearance: 'none',
              }}>
                {meetings.map(m => <option key={m.id} value={m.id} style={{ color: '#0f172a' }}>{m.name}</option>)}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.15rem' }}>
                {session ? (
                  <>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 0 3px rgba(74,222,128,0.25)' }} />
                    <span style={{ fontSize: '0.72rem', color: '#c9e8cc', fontWeight: '600' }}>In progress · started {new Date(session.started_at).toLocaleTimeString()}</span>
                  </>
                ) : (
                  <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)' }}>Not currently in a meeting</span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => setShowParticipants(s => !s)} style={heroGhostBtn}><UsersIcon size={14} /> Participants</button>
            <button onClick={() => setShowNewMeeting(s => !s)} style={heroGhostBtn}><PlusIcon size={14} /> New Meeting</button>
            {!session ? (
              <button onClick={startMeeting} disabled={starting || !meeting} style={{ ...st.primaryBtn, background: `linear-gradient(135deg, ${colors.amber}, #d69a1f)`, boxShadow: '0 2px 10px rgba(201,168,75,0.35)', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                <PlayIcon size={13} />{starting ? 'Starting...' : 'Start Meeting'}
              </button>
            ) : (
              <span style={{ ...heroGhostBtn, cursor: 'default', fontWeight: '700', color: '#fff' }}>Meeting live</span>
            )}
          </div>
        </div>

        {showNewMeeting && <NewMeetingForm onCreate={createMeeting} onCancel={() => setShowNewMeeting(false)} />}
        {showParticipants && meeting && <ParticipantsPanel meetingId={meeting.id} onClose={() => setShowParticipants(false)} />}

        <div style={st.tabStrip}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ ...st.tab(tab === t.key), display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <t.Icon size={14} />{t.label}
            </button>
          ))}
        </div>

        {tab === 'summary' && <LastMeetingSummarySection meetingId={meeting?.id} refreshKey={summaryRefreshKey} />}
        {tab === 'checkin' && <CheckInSection session={session} />}
        {tab === 'transcript' && <TranscriptSection session={session} meetingId={meeting?.id} />}
        {tab === 'goals' && <GoalsSection />}
        {tab === 'metrics' && <ScorecardSection />}
        {tab === 'headlines' && <HeadlinesSection sessionId={session?.id} meetingId={meeting?.id} />}
        {tab === 'todos' && <TodosSection sessionId={session?.id} meetingId={meeting?.id} />}
        {tab === 'issues' && <IssuesSection sessionId={session?.id} meetingId={meeting?.id} />}
        {tab === 'wrapup' && <WrapUpSection session={session} onEnded={onMeetingEnded} />}
      </div>
    </div>
  );
}
